import { Component, lazy, Suspense, useContext, useMemo } from 'react'
import { useTranslate } from 'react-polyglot'
import { HtmlPortalNode, OutPortal } from 'react-reverse-portal'

import { Drawer, OrganizationContext } from 'components'
import config from 'config'
import { Activity } from 'types/Activity'
import { getProductFamilyName } from 'utils/getProductNames'
import ModuleFederationLoader from 'utils/ModuleFederationLoader'
import PartnerToolsDrawer from '../partnerTools/PartnerToolsDrawer'
import CustomSideDrawer from './CustomSideDrawer'

// Lazy imports
const HelpDrawer = lazy(() => import('../helpDrawer/HelpDrawer'))
const NotificationsDrawer = lazy(() => import('../NotificationsDrawer'))

export enum SideDrawerMode {
  none = 'none',
  setup = 'setup',
  setupResolve = 'setupResolve',
  notifications = 'notifications',
  partnerTools = 'partnerTools',
  help = 'help',
  cart = 'cart',
  copilot = 'copilot',
  custom = 'custom',
}

interface SideDrawerProps {
  hasBanner: boolean
  bannerOffset: number
  sideDrawerMode: SideDrawerMode
  activities?: Activity[]
  onCloseDrawer: () => void
  copilotPortalNode?: HtmlPortalNode<Component<any>>
  customSideDrawerContent?: HTMLElement
}

const SideDrawer = ({
  hasBanner,
  bannerOffset,
  sideDrawerMode,
  activities,
  onCloseDrawer,
  copilotPortalNode,
  customSideDrawerContent,
}: SideDrawerProps): JSX.Element => {
  const t = useTranslate()
  const pbx = useContext(OrganizationContext)

  const resolveTitle = getProductFamilyName('gotoresolve')

  const BASE_COPILOT_OFFSET = 55

  const currentTitle = useMemo(() => {
    switch (sideDrawerMode) {
      case SideDrawerMode.help:
        return t('Help')
      case SideDrawerMode.setup:
        return t('Setup')
      case SideDrawerMode.setupResolve:
        return t('Set up %{resolveTitle}', { resolveTitle })
      case SideDrawerMode.notifications:
        return t('Notifications')
      case SideDrawerMode.partnerTools:
        return t('Partner tools')
      case SideDrawerMode.cart:
        return t('Your cart')
      case SideDrawerMode.custom:
        return ' ' // This is to ensure we have the close button
      default:
        return ''
    }
  }, [sideDrawerMode, t, resolveTitle])

  const currentContent = useMemo(() => {
    switch (sideDrawerMode) {
      case SideDrawerMode.help:
        return (
          <Suspense fallback={<></>}>
            <HelpDrawer />
          </Suspense>
        )
      case SideDrawerMode.notifications:
        return (
          <Suspense fallback={<></>}>
            <NotificationsDrawer onCloseDrawer={onCloseDrawer} activities={activities} />
          </Suspense>
        )
      case SideDrawerMode.partnerTools:
        return <PartnerToolsDrawer />
      case SideDrawerMode.setup:
      case SideDrawerMode.setupResolve:
        return (
          <>
            {(pbx?.showJive || pbx?.showResolve) && (
              <ModuleFederationLoader
                module="guidedSetupApp"
                subAppModuleFederation={config.subAppsModuleFederation.uccUnifiedAdminGuidedSetup}
                initSentry={false}
              />
            )}
          </>
        )
      case SideDrawerMode.copilot:
        return (
          <div
            style={{
              height: `calc(100vh - ${hasBanner ? BASE_COPILOT_OFFSET + bannerOffset : BASE_COPILOT_OFFSET}px)`,
            }}
            className="copilot-drawer-content"
          >
            {copilotPortalNode && <OutPortal node={copilotPortalNode} onClose={onCloseDrawer} />}
          </div>
        )
      case SideDrawerMode.cart:
        return (
          <ModuleFederationLoader
            module="cartApp"
            subAppModuleFederation={config.subAppsModuleFederation.uccUnifiedAdminCart}
            initSentry={false}
          />
        )

      case SideDrawerMode.custom:
        if (customSideDrawerContent) {
          return <CustomSideDrawer content={customSideDrawerContent} />
        }
        return null
    }
  }, [
    sideDrawerMode,
    onCloseDrawer,
    activities,
    pbx?.showJive,
    pbx?.showResolve,
    hasBanner,
    bannerOffset,
    copilotPortalNode,
    customSideDrawerContent,
  ])

  return (
    <Drawer
      title={currentTitle}
      open={sideDrawerMode !== SideDrawerMode.none}
      onCloseDrawer={onCloseDrawer}
      bannerMargin={hasBanner}
    >
      {currentContent}
    </Drawer>
  )
}

export default SideDrawer
